import { v4 as uuid } from 'uuid';
import { useEffect, useRef, useState } from 'react';
import { DatoStory, Story } from '../../../types.ts/story';
import VideoCreatorStore from '../../../stores/VideoCreatorStore';
import { SimpleSchemaTypes } from '@datocms/cma-client-browser';
import {
  getExternalUploadsBucketPathKey,
  renameFile,
  uploadFileToS3ExternalUploads,
} from '../../../utility/s3';
import { NEW_STORIES_COMPLETION_QUERY } from '../../../gql/story-gql';
import { useFlagsCombination } from '../../../utility/useFlagsCombination';
import { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import { UserIdentityStore } from '@src/stores-v2/UserIdentityStore';
import { ORIGINAL_VIDEO_LANGUAGE_OPTIONS } from '@src/config/languages';
import { useStoryUploadAnalytics } from './useStoryUploadAnalytics';

export enum Step {
  one = 1,
  two,
  three,
  four,
  five,
}

const MAX_AWS_UPLOAD_SIZE_GB_NO_LIMIT = 100;
const MAX_AWS_UPLOAD_SIZE_GB_LIMIT = 5;
const MAX_AWS_UPLOAD_SIZE_GB = 2.7;
const MAX_DATO_UPLOAD_SIZE_GB = 1;

export type ArtifactUpload = SimpleSchemaTypes.Upload & { _internalId: string };
export type ArtifactFile = File & { _internalId: string };

export type StoryVideoMusicStrategy = 'keep' | 'process';

type ReturnType = {
  step: Step;
  goToNextStep: () => void;
  canGoToStep: (s: number) => boolean;
  handleStoryVideoUpload: (f: File[]) => void;
  handleStoryBRollUpload: (f: File[]) => void;
  storyName: string;
  setStoryName: (s: string) => void;
  storyLanguage: string | undefined;
  setStoryLanguage: (s: string) => void;
  storyType: 'raw' | 'produced' | null;
  setStoryType: (t: 'raw' | 'produced') => void;
  videoMusicStrategy: StoryVideoMusicStrategy;
  setVideoMusicStrategy: (s: StoryVideoMusicStrategy) => void;
  storyArtifactsFiles: ArtifactFile[];
  storyArtifactsVideoFiles: ArtifactFile[];
  storyArtifacts: ArtifactUpload[];
  storyArtifactsVideo: ArtifactUpload[];
  saveArtifactNotes: (internalId: string) => void;
  setArtifactNotes: (internalId: string, notes: string) => void;
  deleteArtifact: (internalId: string) => void;
  saveArtifactVideoNotes: (internalId: string) => void;
  setArtifactVideoNotes: (internalId: string, notes: string) => void;
  deleteArtifactVideo: (internalId: string) => void;
  maxUploadSizeGB: number;
};

type Params = {
  videoCreator: VideoCreatorStore;
  datoClientStore: DatoClientStore;
  userIdentity: UserIdentityStore;
  onStoryInitialized: (s: Story) => void;
  onStoryCreated: (s: Story) => void;
  onStoryProgress: (percent: number, storyId?: string) => void;
  onError: (...data: any[]) => void;
};

export function useStoryCreator(params: Params): ReturnType {
  const videoCreator = params.videoCreator;
  const datoClientStore = params.datoClientStore;
  const currentRole = params.userIdentity.currentRole;
  const {
    storyUploadUseAws,
    enableLargeFileSizeUpload,
    enableNoLimitFileSizeUpload,
  } = useFlagsCombination(currentRole);
  const storyTracker = useStoryUploadAnalytics({});

  const [step, setStep] = useState<Step>(1);
  const [story, setStory] = useState<Story>();

  // step 1 data
  const [storyVideoFile, setStoryVideoFile] = useState<File | null>(null);
  const blankStoryRef = useRef<DatoStory | null>(null);
  const storyVideoUploadCompleted = useRef<boolean>(false);
  const lastTimeStoryLogUpdatedRef = useRef<number>(0);
  const lastUploadPercentLoggedRef = useRef<number>(0);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<boolean | null>(null);

  // step 2 data
  const [storyType, setStoryType] = useState<'raw' | 'produced' | null>(null);
  const [videoMusicStrategy, setVideoMusicStrategy] =
    useState<StoryVideoMusicStrategy>('keep');

  // step 3 data
  const [storyName, setStoryName] = useState<string>('');
  const [storyTellerName, _setStoryTellerName] = useState<string>(
    videoCreator?.organization?.title || 'no_org',
  );
  const [storyTellerTitle, _setStoryTellerTitle] = useState<string>(
    'Organization storyteller',
  );
  const [storyLanguage, setStoryLanguage] = useState<string | undefined>(() => {
    const defaultLanguage =
      videoCreator?.organization?.defaultLanguage || 'en-us';
    return videoCreator?.organization?.allowLangSelectAtUserStoryUpload
      ? (
          ORIGINAL_VIDEO_LANGUAGE_OPTIONS.find(
            (o) => o.value === defaultLanguage,
          ) || ORIGINAL_VIDEO_LANGUAGE_OPTIONS[0]
        ).value
      : undefined;
  });

  // step 4 data
  const [storyArtifactsFiles, setStoryArtifactsFiles] = useState<
    ArtifactFile[]
  >([]);
  const [storyArtifacts, setStoryArtifacts] = useState<ArtifactUpload[]>([]);
  const [storyArtifactsVideoFiles, setStoryArtifactsVideoFiles] = useState<
    ArtifactFile[]
  >([]);
  const [storyArtifactsVideo, setStoryArtifactsVideo] = useState<
    ArtifactUpload[]
  >([]);
  const [bRollReady, setBRollReady] = useState<boolean>(false);
  const [storyUploadProcessingStarted, setStoryUploadProcessingStarted] =
    useState<boolean>(false);
  const storyArtifactsRef = useRef<ArtifactUpload[]>([]);
  const storyArtifactsFilesRef = useRef<ArtifactFile[]>([]);
  const storyArtifactsVideoRef = useRef<ArtifactUpload[]>([]);
  const storyArtifactsVideoFilesRef = useRef<ArtifactFile[]>([]);
  const lastProgressPercentRef = useRef<number>(0);
  const [nextCallback, setNextCallback] = useState<'init' | 'create'>('init');

  console.log('useStoryCreator initialized');

  // option to remove music was not selected - proceed with regular video upload
  useEffect(() => {
    if (
      story &&
      uploadedFileUrl &&
      videoMusicStrategy &&
      !storyVideoUploadCompleted.current
    ) {
      storyVideoUploadCompleted.current = true;
      (async () => {
        await startProcessingUploadedVideo();
      })();
      setStoryUploadProcessingStarted(true);
    }
  }, [uploadedFileUrl, story, storyType, videoMusicStrategy]);

  useEffect(() => {
    if (step === Step.four && !story) {
      handleStoryCreate();
    }
    if (step === Step.five && story?.id && nextCallback === 'init') {
      // lastProgressPercentRef.current = Infinity; // stop progress updates after story is initialized
      (async () => {
        params.onStoryInitialized({
          ...story,
          ...(await getStoryUpdates()),
        });
        setNextCallback('create');
      })();
    }
  }, [step, story?.id, nextCallback]);

  useEffect(() => {
    console.debug('step', step, storyUploadProcessingStarted, nextCallback);
    if (
      step === Step.five &&
      story &&
      storyUploadProcessingStarted &&
      bRollReady &&
      nextCallback === 'create'
    ) {
      (async () => {
        params.onStoryCreated({
          ...story,
          ...(await getStoryUpdates()),
        });
      })();
    }
  }, [step, story, storyUploadProcessingStarted, bRollReady, nextCallback]);

  useEffect(() => {
    console.log('storyArtifacts changed:', storyArtifacts);
    // to prevent methods' closure from capturing stale state
    storyArtifactsRef.current = storyArtifacts;
    storyArtifactsFilesRef.current = storyArtifactsFiles;
    storyArtifactsVideoRef.current = storyArtifactsVideo;
    storyArtifactsVideoFilesRef.current = storyArtifactsVideoFiles;

    // b-roll is uploaded when story artifacts' length matches files' length
    if (
      storyArtifacts.length === storyArtifactsFiles.length &&
      storyArtifactsVideo.length === storyArtifactsVideoFiles.length
    ) {
      // if we are at the last step, time to update story with uploaded b-roll and mark b-roll as ready
      // doing it at the last step makes it less error prone, because b-roll can change multiple times at step 4
      if (story && step === Step.five) {
        (async () => {
          if (
            storyArtifactsFiles.length > 0 ||
            storyArtifactsVideoFiles.length > 0
          ) {
            try {
              const storyUpdate = await videoCreator.updateStory({
                id: story.id,
                storyArtifacts: storyArtifacts.map((a) => ({
                  id: a.id,
                })),
                storyArtifactsVideo: storyArtifactsVideo.map((a) => ({
                  id: a.id,
                })),
              });
              setStory({
                ...story,
                ...storyUpdate,
              });
            } catch (error) {
              storyTracker.analyticsLog(
                'error',
                'Error updating story with artifacts',
                { error },
              );
            }
          }
          setBRollReady(true);
        })();
      }
      // initial b-roll upload is done - time to update progress, prematurely
      // TODO TEMP disabled, needs to be revisited
      // if (
      //   storyArtifactsFiles.length > 0 ||
      //   storyArtifactsVideoFiles.length > 0 ||
      //   step === Step.five
      // ) {
      //   callStoryProgressCbOnceFor(50);
      // }
    }
  }, [
    storyArtifacts,
    storyArtifactsFiles,
    storyArtifactsVideo,
    storyArtifactsVideoFiles,
    step,
    story?.id,
  ]);

  const getStoryUpdates = async (): Promise<Partial<Story>> => {
    try {
      const queryResult: {
        allStories: Partial<Story>[];
      } = await datoClientStore.gqlClient!.request(
        NEW_STORIES_COMPLETION_QUERY,
        {
          ids: [story!.id],
        },
      );
      return queryResult.allStories[0];
    } catch (err) {
      console.warn('Error fetching story updates', err);
      return {};
    }
  };

  const callStoryProgressCbOnceFor = (percent: number, storyId?: string) => {
    console.debug('callStoryProgressCbOnceFor', percent);
    if (percent > lastProgressPercentRef.current || percent === 100) {
      lastProgressPercentRef.current = percent;
      params.onStoryProgress(percent, storyId);
    }
  };

  const handleVideoUploadProgress = async (
    progress: number,
    done?: boolean,
  ) => {
    const timestamp = Date.now();
    if (
      !storyVideoUploadCompleted.current &&
      (timestamp - lastTimeStoryLogUpdatedRef.current > 10000 ||
        progress - lastUploadPercentLoggedRef.current > 10 ||
        done)
    ) {
      try {
        lastTimeStoryLogUpdatedRef.current = timestamp;
        lastUploadPercentLoggedRef.current = progress;
        await datoClientStore.storyRepository!.updateStoryStepsLog({
          id: blankStoryRef.current!.uploadLog,
          initial_upload: JSON.stringify({
            ...(done ? { status: 'success' } : {}),
            progress,
            updatedAt: timestamp,
          }),
        });
      } catch (err) {
        console.warn(
          'Error updating story upload log with video upload progress',
          err,
        );
        lastTimeStoryLogUpdatedRef.current = timestamp - 10000;
        lastUploadPercentLoggedRef.current = progress - 10;
      }
    }
    callStoryProgressCbOnceFor(
      Math.round(progress / 2),
      blankStoryRef.current?.id,
    );
  };

  const formatCurrentUserData = () => {
    const userData = params.userIdentity.getUserIdentity();
    if (!userData) {
      return null;
    }
    return (({ id, name, email, type }) => ({ id, name, email, type }))(
      userData,
    );
  };

  const handleStoryCreate = async () => {
    if (!blankStoryRef.current) {
      setTimeout(() => {
        handleStoryCreate();
      }, 5 * 1000);
      return;
    }

    try {
      const storyUpdate = await videoCreator.updateStory({
        id: blankStoryRef.current.id,
        title: storyName,
        storyTeller: blankStoryRef.current.storyTeller,
        storyType: storyType!,
        originalVideoLanguage: storyLanguage,
      });
      setStory({
        ...blankStoryRef.current,
        ...storyUpdate,
      } as Story);
    } catch (error) {
      params.onError(error);
      storyTracker.analyticsLog(
        'error',
        'Error updating story with name and storyteller',
        { error },
      );
    }
  };

  const createBlankStory = async (): Promise<DatoStory> => {
    return await videoCreator.createStory({
      title: `user-upload-${uuid()}`,
      storyType: storyType || undefined,
      storyTeller: {
        name: storyTellerName,
        id: (
          await videoCreator.upsertStoryTeller({
            name: storyTellerName,
            title: storyTellerTitle,
          })
        ).id,
      },
      primaryShowcase: videoCreator.organization,
      externalUploadLog: { uploadedBy: formatCurrentUserData() },
      byExternalUser: true,
      useAws: storyUploadUseAws,
    });
  };

  const startProcessingUploadedVideo = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/story/upload-task`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transactionId: storyTracker.transactionId,
            analyticsContext: {
              ...storyTracker.analyticsContext,
              story: {
                id: story!.id,
                name: storyName,
              },
            },
            storyId: story!.id,
            showcaseId: videoCreator.organization?.id,
            userData: formatCurrentUserData(),
            s3Key: getExternalUploadsBucketPathKey(
              renameFile(storyVideoFile!, story!.id).name,
              videoCreator.organization?.id,
            ),
            audioProcessPreset:
              videoMusicStrategy === 'process'
                ? storyType === 'produced'
                  ? 'REMOVE_MUSIC'
                  : 'CORRECT_AUDIO'
                : undefined,
          }),
        },
      );
      if (!response.ok) {
        throw new Error(`Not ok: ${response.status}`);
      }
    } catch (error) {
      params.onError(error);
      storyTracker.analyticsLog('error', 'Error calling video processing API', {
        error,
      });
    }
    if (process.env.REACT_APP_ENABLE_OLD_DATO_PROCESSING === 'true') {
      console.debug(
        'REACT_APP_ENABLE_OLD_DATO_PROCESSING',
        process.env.REACT_APP_ENABLE_OLD_DATO_PROCESSING === 'true',
      );
      if (videoMusicStrategy !== 'process') {
        await startOldVideoProcessingPipeline();
      }
    }
  };

  const startOldVideoProcessingPipeline = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/story/upload-task-old`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            storyId: story!.id,
            s3Key: getExternalUploadsBucketPathKey(
              renameFile(storyVideoFile!, story!.id).name,
              videoCreator.organization?.id,
            ),
          }),
        },
      );
      if (!response.ok) {
        throw new Error(`Not ok: ${response.status}`);
      }
    } catch (err) {
      console.error('Error when triggering old video processing pipeline', err);
      console.warn('Error suppressing');
    }
  };

  const handleStoryVideoUpload = async (files: File[]) => {
    if (!files.length) {
      console.warn('No files at AddStoriesModal handleStoryVideoUpload');
      return;
    }
    setStoryVideoFile(files[0]);
    handleVideoUploadProgress(1);
    goToNextStep();

    storyTracker.trackInit({
      fileSize: files[0].size,
      fileType: files[0].type,
    });

    try {
      blankStoryRef.current = await createBlankStory();
      storyTracker.analyticsLog('info', 'New story created', {
        story: {
          id: blankStoryRef.current.id,
        },
      });
      const tBegin = performance.now();
      await uploadFileToS3ExternalUploads(
        renameFile(files[0], blankStoryRef.current.id),
        videoCreator.organization?.id,
        {
          onUploadProgress: (progress, done, total) => {
            console.debug('Upload file progress', progress, done, total);
            handleVideoUploadProgress(progress, done);
            if (done) {
              storyTracker.analyticsLog(
                'info',
                'Story video upload completed',
                {
                  bytesUploaded: total,
                  secondsElapsed: (performance.now() - tBegin) / 1000,
                },
              );
              setUploadedFileUrl(true);
            }
          },
        },
      );
    } catch (error) {
      storyTracker.analyticsLog(
        'error',
        'Story creation or video upload failed',
        {
          error,
        },
      );
      params.onError(error);
    }
  };

  const handleStoryBRollUpload = async (files: File[]) => {
    console.log('handleStoryBRollUpload', files);
    const imageFiles = files.filter((f) =>
      f.type.includes('image'),
    ) as ArtifactFile[];
    imageFiles.forEach((f) => {
      f._internalId = uuid();
    });

    const videoFiles = files.filter((f) =>
      f.type.includes('video'),
    ) as ArtifactFile[];
    videoFiles.forEach((f) => {
      f._internalId = uuid();
    });

    let newStoryArtifactsFiles =
      storyArtifactsFilesRef.current.concat(imageFiles);
    setStoryArtifactsFiles(newStoryArtifactsFiles);
    let newStoryArtifactsVideoFiles =
      storyArtifactsVideoFilesRef.current.concat(videoFiles);
    setStoryArtifactsVideoFiles(newStoryArtifactsVideoFiles);

    let newStoryArtifacts = [...storyArtifactsRef.current];

    for (const file of imageFiles) {
      try {
        const upload =
          await datoClientStore.datoClient?.uploads.createFromFileOrBlob({
            fileOrBlob: file,
            filename: file.name,
          });
        if (upload) {
          newStoryArtifacts = newStoryArtifacts.concat({
            ...upload,
            _internalId: file._internalId,
          });
          setStoryArtifacts(newStoryArtifacts);
          console.log(
            'handleStoryBRollUpload setStoryArtifacts',
            newStoryArtifacts,
          );
        }
      } catch (err) {
        newStoryArtifactsFiles = newStoryArtifactsFiles.filter(
          (f) => f._internalId !== file._internalId,
        );
        setStoryArtifactsFiles(newStoryArtifactsFiles);
        storyTracker.analyticsLog('warn', 'Error uploading image b-roll', {
          error: err,
        });
      }
    }

    let newStoryArtifactsVideo = [...storyArtifactsVideoRef.current];

    for (const file of videoFiles) {
      try {
        const upload =
          await datoClientStore.datoClient?.uploads.createFromFileOrBlob({
            fileOrBlob: file,
            filename: file.name,
          });
        if (upload) {
          newStoryArtifactsVideo = newStoryArtifactsVideo.concat({
            ...upload,
            _internalId: file._internalId,
          });
          setStoryArtifactsVideo(newStoryArtifactsVideo);
        }
      } catch (err) {
        newStoryArtifactsVideoFiles = newStoryArtifactsVideoFiles.filter(
          (f) => f._internalId !== file._internalId,
        );
        setStoryArtifactsVideoFiles(newStoryArtifactsVideoFiles);
        storyTracker.analyticsLog('warn', 'Error uploading video b-roll', {
          error: err,
        });
      }
    }
  };

  const goToNextStep = () => {
    setStep(step + 1);
  };

  const canGoToStep = (nextStep: number): boolean => {
    if (nextStep === Step.two) {
      return !!storyVideoFile;
    }
    if (nextStep === Step.three) {
      return !!storyType;
    }
    if (nextStep === Step.four) {
      return !!storyName && !!(storyTellerName || '').trim();
    }
    if (nextStep === Step.five) {
      return !!story;
    }
    return true;
  };

  const saveArtifactNotes = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsRef.current.find(
      (a) => a._internalId === internalId,
    );
    await doSaveArtifactNotes(artifact);
  };

  const doSaveArtifactNotes = async (artifact?: ArtifactUpload) => {
    if (!artifact) {
      return;
    }
    try {
      await datoClientStore.assetRepository?.update(artifact.id, {
        title: artifact.notes || '',
        alt: artifact.notes || '',
      });
    } catch (err) {
      storyTracker.analyticsLog('warn', 'Error saving b-roll notes', {
        error: err,
      });
    }
  };

  const setArtifactNotes = (internalId: string, notes: string): void => {
    setStoryArtifacts((prev) =>
      prev.map((a) => (a._internalId === internalId ? { ...a, notes } : a)),
    );
  };

  const deleteArtifact = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsRef.current.find(
      (a) => a._internalId === internalId,
    );
    if (!artifact) {
      return;
    }

    setStoryArtifacts(
      storyArtifactsRef.current.filter((a) => a._internalId !== internalId),
    );
    setStoryArtifactsFiles(
      storyArtifactsFilesRef.current.filter(
        (f) => f._internalId !== internalId,
      ),
    );

    try {
      await datoClientStore.assetRepository?.delete(artifact.id);
    } catch (err) {
      storyTracker.analyticsLog('warn', 'Error deleting b-roll', {
        error: err,
      });
    }
  };

  const saveArtifactVideoNotes = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsVideoRef.current.find(
      (a) => a._internalId === internalId,
    );
    await doSaveArtifactNotes(artifact);
  };

  const setArtifactVideoNotes = (internalId: string, notes: string): void => {
    setStoryArtifactsVideo(
      storyArtifactsVideoRef.current.map((a) =>
        a._internalId === internalId ? { ...a, notes } : a,
      ),
    );
  };

  const deleteArtifactVideo = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsVideoRef.current.find(
      (a) => a._internalId === internalId,
    );
    if (!artifact) {
      return;
    }

    setStoryArtifactsVideo(
      storyArtifactsVideoRef.current.filter(
        (a) => a._internalId !== internalId,
      ),
    );
    setStoryArtifactsVideoFiles(
      storyArtifactsVideoFilesRef.current.filter(
        (f) => f._internalId !== internalId,
      ),
    );

    try {
      await datoClientStore.assetRepository?.delete(artifact.id);
    } catch (err) {
      storyTracker.analyticsLog('warn', 'Error deleting b-roll', {
        error: err,
      });
    }
  };

  console.log('Artifacts', storyArtifacts);

  return {
    step,
    goToNextStep,
    canGoToStep,
    handleStoryVideoUpload,
    handleStoryBRollUpload,
    storyName,
    setStoryName,
    storyLanguage,
    setStoryLanguage,
    storyType,
    setStoryType,
    videoMusicStrategy,
    setVideoMusicStrategy,
    storyArtifactsFiles,
    storyArtifactsVideoFiles,
    storyArtifacts,
    storyArtifactsVideo,
    saveArtifactNotes,
    setArtifactNotes,
    deleteArtifact,
    saveArtifactVideoNotes,
    setArtifactVideoNotes,
    deleteArtifactVideo,
    maxUploadSizeGB: storyUploadUseAws
      ? enableNoLimitFileSizeUpload
        ? MAX_AWS_UPLOAD_SIZE_GB_NO_LIMIT
        : enableLargeFileSizeUpload
          ? MAX_AWS_UPLOAD_SIZE_GB_LIMIT
          : MAX_AWS_UPLOAD_SIZE_GB
      : MAX_DATO_UPLOAD_SIZE_GB,
  };
}
